<script setup lang="ts">
/*
 * Side-sliding drawer. Common uses: pay-input edit, employee inspector,
 * filter panel. Built on Headless UI Dialog; slides in from `side` prop.
 */
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { X } from 'lucide-vue-next';
import { useDialogReturnFocus } from '@/composables/useDialogReturnFocus';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    subtitle?: string;
    side?: 'right' | 'left';
    width?: string;
    /**
     * Allow an outside (overlay) click or ESC to dismiss the drawer. Defaults to
     * `false`: a drawer closes ONLY via the × button (or the consumer emitting
     * `close`), so an accidental click outside never discards a mid-fill form.
     * Set `true` only for throwaway read-only panels where click-away-to-close
     * is genuinely wanted.
     */
    dismissible?: boolean;
    /**
     * Accessible name for the scrolling body. When set, the body becomes a
     * keyboard-focusable named region (`tabindex=0`, `role=region`) so a
     * read-only panel with no focusable content can still be scrolled from the
     * keyboard (axe `scrollable-region-focusable`). Omit for form drawers.
     */
    bodyLabel?: string;
    /**
     * Element to focus once the drawer has closed. Defaults to whatever was
     * focused when it opened (the trigger) — WCAG 2.4.3.
     */
    returnFocusTo?: HTMLElement | null;
  }>(),
  { side: 'right', width: '420px', dismissible: false, returnFocusTo: null },
);

const emit = defineEmits<{
  close: [];
  /**
   * The close transition finished and the dialog is gone. Open a follow-up
   * dialog (e.g. an edit modal) here rather than in the same tick as closing:
   * two Headless UI dialogs swapping at once leave the new one unresponsive.
   */
  afterLeave: [];
}>();

// Headless UI fires @close for BOTH ESC and backdrop-click. By default we swallow
// it so neither dismisses the drawer — only the explicit × button (or a footer
// Close) can. Opt in with `dismissible` for read-only panels.
function onDialogClose() {
  if (props.dismissible) emit('close');
}

// Focus goes back to the opener (or `returnFocusTo`) BEFORE `afterLeave` is
// emitted, so a follow-up dialog opened there captures that element as its
// own opener and takes focus over it.
const returnFocus = useDialogReturnFocus(
  () => props.open,
  () => props.returnFocusTo,
);

async function onAfterLeave(): Promise<void> {
  await returnFocus.restore();
  emit('afterLeave');
}

const sideClass = props.side === 'left' ? 'left-0' : 'right-0';
const enterFrom = props.side === 'left' ? '-translate-x-full' : 'translate-x-full';
</script>

<template>
  <TransitionRoot :show="open" as="template" @after-leave="onAfterLeave">
    <Dialog as="div" class="relative z-50" @close="onDialogClose">
      <TransitionChild
        as="template"
        enter="duration-150 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-100 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-app-fg/40" aria-hidden="true" />
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        :enter-from="enterFrom"
        enter-to="translate-x-0"
        leave="duration-150 ease-in"
        leave-from="translate-x-0"
        :leave-to="enterFrom"
      >
        <DialogPanel
          :class="[
            'fixed inset-y-0 flex h-full flex-col border-app-border bg-app-surface shadow-popup',
            sideClass,
            side === 'left' ? 'border-r' : 'border-l',
          ]"
          :style="{ width }"
        >
          <!-- Proto-verbatim form header: brand violet gradient band + white title/
               subtitle + a bordered white × (matches the Modal brand header + the
               prototype's Add-Person drawer). -->
          <!-- role="none": a <header> inside a dialog must not expose a banner
               landmark — two stacked dialogs would otherwise show duplicate
               banners (axe landmark-no-duplicate-banner). -->
          <header
            role="none"
            class="flex shrink-0 items-start justify-between gap-3 bg-gradient-to-r from-primary-600 to-primary px-6 py-4 text-white"
          >
            <div class="flex flex-col gap-0.5">
              <DialogTitle v-if="title" class="text-lg font-bold text-white">
                {{ title }}
              </DialogTitle>
              <p v-if="subtitle" class="text-xs leading-[1.5] text-white/90">
                {{ subtitle }}
              </p>
            </div>
            <button
              type="button"
              class="inline-flex cursor-pointer items-center justify-center rounded-lg p-1 text-white/90 ring-1 ring-inset ring-white/40 transition-colors hover:bg-white/15 hover:text-white hover:ring-white/60"
              aria-label="Close"
              @click="$emit('close')"
            >
              <X class="h-4 w-4" aria-hidden="true" />
            </button>
          </header>
          <div
            class="flex-1 overflow-y-auto px-5 py-4"
            :role="bodyLabel ? 'region' : undefined"
            :tabindex="bodyLabel ? 0 : undefined"
            :aria-label="bodyLabel || undefined"
          >
            <slot />
          </div>
          <footer
            v-if="$slots.footer"
            class="flex items-center justify-end gap-2 border-t border-app-border bg-slate-50/50 px-5 py-3"
          >
            <slot name="footer" />
          </footer>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>
